import React, { useState, useEffect, useRef } from 'react';
import { CarerSupportResult } from '../../types/index';

interface CarerSupportAssessmentProps {
  onComplete: (result: CarerSupportResult) => void;
  onNext: () => void;
}

interface CarerSupportResponses {
  supportAdequacy?: boolean;
  hasConfidence?: boolean;
  physicalImpact?: boolean;
  mentalImpact?: boolean;
  financialImpact?: boolean;
  socialImpact?: boolean;
}

type CarerSupportStep = 
  | 'intro'
  | 'adequacy_question'
  | 'confidence_question'
  | 'impact_question'
  | 'results'
  | 'carer_correction'
  | 'completed';

const CarerSupportAssessment: React.FC<CarerSupportAssessmentProps> = ({ onComplete, onNext }) => {
  const [currentStep, setCurrentStep] = useState<CarerSupportStep>('intro');
  const [responses, setResponses] = useState<Partial<CarerSupportResponses>>({});
  const hasCompletedRef = useRef<boolean>(false);
  const [carerCorrection, setCarerCorrection] = useState({
    supportAdequacyCorrect: null as boolean | null,
    physicalImpactCorrect: null as boolean | null,
    mentalImpactCorrect: null as boolean | null,
    financialImpactCorrect: null as boolean | null,
    socialImpactCorrect: null as boolean | null
  });

  const evaluateCarerSupport = (testResponses?: Partial<CarerSupportResponses>): CarerSupportResult => {
    const currentResponses = testResponses || responses;
    const hasNegativeImpact = Boolean(
      currentResponses.physicalImpact ||
      currentResponses.mentalImpact ||
      currentResponses.financialImpact ||
      currentResponses.socialImpact
    );

    const supportNeedsMet = currentResponses.supportAdequacy !== false;
    const hasConfidence = currentResponses.hasConfidence !== false;
    const needsAttention = !supportNeedsMet || !hasConfidence || hasNegativeImpact;

    return {
      status: needsAttention ? 'NEEDS_ATTENTION' : 'SUPPORTED',
      supportNeedsMet: supportNeedsMet,
      hasConfidence: hasConfidence,
      negativeImpacts: {
        physical: responses.physicalImpact || false,
        mental: responses.mentalImpact || false,
        financial: responses.financialImpact || false,
        social: responses.socialImpact || false,
      },
      responses: currentResponses, // 添加原始responses数据
      recommendations: needsAttention ? ["ICOPE-C12-S12.1-CarerSupport-Overview"] : []
    };
  };

  const handleAdequacyResponse = (isAdequate: boolean) => {
    setResponses(prev => ({ 
      ...prev, 
      supportAdequacy: isAdequate
    }));
    
    // 回答支持充分性问题后，进入照护信心评估
    setCurrentStep('confidence_question');
  };

  const handleConfidenceResponse = (hasConfidence: boolean) => {
    setResponses(prev => ({ 
      ...prev, 
      hasConfidence: hasConfidence
    }));
    
    // 照护信心评估完成后，进入照护负担影响评估
    setCurrentStep('impact_question');
  };

  const handleImpactResponse = (impact: keyof Omit<CarerSupportResponses, 'supportAdequacy' | 'hasConfidence'>, value: boolean) => {
    setResponses(prev => ({
      ...prev,
      [impact]: value
    }));
  };

  const completeImpactAssessment = () => {
    setCurrentStep('results');
  };

  const handleResultsConfirm = () => {
    if (!hasCompletedRef.current) {
      hasCompletedRef.current = true;
      const result = evaluateCarerSupport();
      onComplete(result);
      setCurrentStep('completed');
    }
  };

  const handleCarerCorrection = (
    supportAdequacyCorrect: boolean,
    physicalImpactCorrect: boolean,
    mentalImpactCorrect: boolean,
    financialImpactCorrect: boolean,
    socialImpactCorrect: boolean
  ) => {
    // 基于修正构建正确的响应数据
    const correctedResponses = {
      supportAdequacy: supportAdequacyCorrect,
      physicalImpact: !physicalImpactCorrect,
      mentalImpact: !mentalImpactCorrect,
      financialImpact: !financialImpactCorrect,
      socialImpact: !socialImpactCorrect
    };
    
    const hasNegativeImpact = Boolean(
      correctedResponses.physicalImpact ||
      correctedResponses.mentalImpact ||
      correctedResponses.financialImpact ||
      correctedResponses.socialImpact
    );

    const supportNeedsMet = correctedResponses.supportAdequacy !== false;
    const hasConfidence = true; // 假设有信心，因为这通常在adequacy中体现
    const needsAttention = !supportNeedsMet || hasNegativeImpact;

    const correctedResult: CarerSupportResult = {
      status: needsAttention ? 'NEEDS_ATTENTION' : 'SUPPORTED',
      supportNeedsMet: supportNeedsMet,
      hasConfidence: hasConfidence,
      negativeImpacts: {
        physical: correctedResponses.physicalImpact,
        mental: correctedResponses.mentalImpact,
        financial: correctedResponses.financialImpact,
        social: correctedResponses.socialImpact,
      },
      responses: correctedResponses, // 添加修正后的responses数据
      recommendations: needsAttention ? ["ICOPE-C12-S12.1-CarerSupport-Overview"] : [],
      carerCorrected: true
    };
    
    if (!hasCompletedRef.current) {
      hasCompletedRef.current = true;
      onComplete(correctedResult);
      setCurrentStep('completed');
    }
  };

  useEffect(() => {
    if (currentStep === 'completed') {
      const timer = setTimeout(() => onNext(), 2000);
      return () => clearTimeout(timer);
    }
  }, [currentStep, onNext]);

  const renderIntro = () => (
    <div className="text-center space-y-4 p-4">
      <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m3 5.197H9m0 0v-5.197a6 6 0 013 0V21z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900">照护者支持评估</h2>
      <p className="text-gray-600">此部分需要由您的主要照护者回答</p>
      
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-left">
        <h3 className="font-semibold text-gray-900 mb-2">关于此评估：</h3>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• 评估照护者的支持充分性和影响</li>
          <li>• 包含支持感受和负面影响评估</li>
          <li>• 帮助识别照护者是否需要额外支持</li>
          <li>• 请照护者根据真实感受诚实回答</li>
        </ul>
      </div>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
        <p className="text-sm text-yellow-800">
          <strong>请注意：</strong>为了获得真实反馈，建议让照护者在没有压力的情况下独立完成此部分。
        </p>
      </div>
      <button 
        onClick={() => setCurrentStep('adequacy_question')} 
        className="w-full bg-teal-600 text-white py-3 rounded-lg hover:bg-teal-700 transition-colors"
      >
        开始评估
      </button>
    </div>
  );

  const renderAdequacyQuestion = () => (
    <div className="p-4 space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl font-bold text-teal-600">1</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">照护者支持评估</h2>
        <p className="text-gray-600">问题 1 / 3</p>
      </div>

      <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">作为照护者，您觉得自己获得了足够的支持吗？</h3>
        <p className="text-sm text-gray-600 mb-6">包括来自家人、朋友、社区或专业机构的支持</p>
        
        <div className="space-y-3">
          <button onClick={() => handleAdequacyResponse(true)} className="w-full p-4 text-left bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors">
            <div className="flex items-center justify-between">
              <span className="font-medium text-green-800">是的，我获得了足够的支持</span>
              <span className="text-sm text-green-600">支持充分</span>
            </div>
          </button>
          <button onClick={() => handleAdequacyResponse(false)} className="w-full p-4 text-left bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
            <div className="flex items-center justify-between">
              <span className="font-medium text-red-800">不，我觉得支持不足</span>
              <span className="text-sm text-red-600">需要更多支持</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const renderConfidenceQuestion = () => (
    <div className="p-4 space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl font-bold text-teal-600">2</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">照护信心评估</h2>
        <p className="text-gray-600">问题 2 / 3</p>
      </div>

      <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">您对自己的照护能力是否有信心？</h3>
        <p className="text-sm text-gray-600 mb-6">包括处理日常照护任务、应对突发情况等</p>
        
        <div className="space-y-3">
          <button 
            onClick={() => handleConfidenceResponse(true)} 
            className="w-full p-4 text-left bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-green-800">是的，我对照护工作有信心</span>
              <span className="text-sm text-green-600">有信心</span>
            </div>
          </button>
          <button 
            onClick={() => handleConfidenceResponse(false)} 
            className="w-full p-4 text-left bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-red-800">不，我对照护工作缺乏信心</span>
              <span className="text-sm text-red-600">缺乏信心</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const renderImpactQuestion = () => (
    <div className="p-4 space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl font-bold text-teal-600">3</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">照护负担影响评估</h2>
        <p className="text-gray-600">问题 3 / 3</p>
      </div>

      <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">照护工作是否对您在以下方面造成了负面影响？</h3>
        <p className="text-sm text-gray-600 mb-6">请选择所有符合的选项，可以多选。</p>
        
        <div className="space-y-3">
          {Object.keys(impacts).map(key => (
            <label key={key} className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                className="h-5 w-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                checked={responses[key as keyof typeof impacts] || false}
                onChange={e => handleImpactResponse(key as keyof typeof impacts, e.target.checked)}
              />
              <span className="ml-3 text-gray-700">{impacts[key as keyof typeof impacts]}</span>
            </label>
          ))}
        </div>
        
        <button
          onClick={completeImpactAssessment}
          className="w-full mt-6 bg-teal-600 text-white py-3 rounded-lg hover:bg-teal-700 transition-colors"
        >
          完成评估
        </button>
      </div>
    </div>
  );

  const impacts = {
    physicalImpact: '身体健康负担',
    mentalImpact: '心理健康压力',
    financialImpact: '经济负担',
    socialImpact: '影响社交生活'
  };

  const renderResults = () => {
    const result = evaluateCarerSupport();
    const needsAttention = result.status === 'NEEDS_ATTENTION';
    const supportAdequate = responses.supportAdequacy === true;
    
    return (
      <div className="p-4 space-y-6">
        <div className="text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            needsAttention ? 'bg-orange-100' : 'bg-green-100'
          }`}>
            <svg className={`w-8 h-8 ${needsAttention ? 'text-orange-600' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">评估完成</h2>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">照护者支持评估结果：</h3>
          
          <div className={`p-4 rounded-lg border mb-4 ${
            needsAttention ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'
          }`}>
            <div className="text-center">
              <div className={`text-2xl font-bold mb-1 ${
                needsAttention ? 'text-orange-600' : 'text-green-600'
              }`}>
                {needsAttention ? '需要关注' : '支持良好'}
              </div>
              <p className="text-gray-600">照护者支持状态</p>
            </div>
          </div>

          {/* 用户具体答案显示 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-gray-900 mb-3">您的具体回答：</h4>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">支持需求满足程度：</span>
                <span className={`font-medium px-2 py-1 rounded ${
                  responses.supportAdequacy === true 
                    ? 'bg-green-100 text-green-700' 
                    : responses.supportAdequacy === false
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-700'
                }`}>
                  {responses.supportAdequacy === true ? '支持充分' : 
                   responses.supportAdequacy === false ? '支持不足' : '未回答'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">照护信心水平：</span>
                <span className={`font-medium px-2 py-1 rounded ${
                  responses.hasConfidence === true 
                    ? 'bg-green-100 text-green-700' 
                    : responses.hasConfidence === false
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-700'
                }`}>
                  {responses.hasConfidence === true ? '有信心' : 
                   responses.hasConfidence === false ? '缺乏信心' : '未回答'}
                </span>
              </div>
              <div className="border-t pt-3">
                <span className="text-gray-700 block mb-2">照护负担影响评估：</span>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className={`p-2 rounded flex items-center justify-between ${
                    responses.physicalImpact ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                  }`}>
                    <span>身体健康</span>
                    <span>{responses.physicalImpact ? '有负面影响' : '无负面影响'}</span>
                  </div>
                  <div className={`p-2 rounded flex items-center justify-between ${
                    responses.mentalImpact ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                  }`}>
                    <span>心理健康</span>
                    <span>{responses.mentalImpact ? '有负面影响' : '无负面影响'}</span>
                  </div>
                  <div className={`p-2 rounded flex items-center justify-between ${
                    responses.financialImpact ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                  }`}>
                    <span>经济状况</span>
                    <span>{responses.financialImpact ? '有负面影响' : '无负面影响'}</span>
                  </div>
                  <div className={`p-2 rounded flex items-center justify-between ${
                    responses.socialImpact ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                  }`}>
                    <span>社交生活</span>
                    <span>{responses.socialImpact ? '有负面影响' : '无负面影响'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`p-3 rounded ${
            needsAttention ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'
          }`}>
            <h4 className={`font-semibold mb-2 ${
              needsAttention ? 'text-orange-600' : 'text-green-600'
            }`}>评估说明：</h4>
            <p className={`text-sm ${
              needsAttention ? 'text-orange-700' : 'text-green-700'
            }`}>
              {supportAdequate 
                ? '照护者感到支持充分，这是照护工作可持续性的重要基础。请继续保持良好的支持网络。'
                : needsAttention 
                  ? '检测到照护者支持方面需要关注的问题，建议寻求额外支持资源以减轻照护负担。'
                  : '照护者支持状况总体良好，但建议持续关注支持需求的变化。'}
            </p>
          </div>
          
          <div className="mt-6 flex flex-col gap-3">
            <button
              onClick={handleResultsConfirm}
              className="w-full bg-teal-600 text-white py-3 px-6 rounded-lg hover:bg-teal-700 transition-colors font-medium"
            >
              确认结果，完成测试
            </button>
            <button
              onClick={() => setCurrentStep('carer_correction')}
              className="w-full bg-orange-500 text-white py-3 px-6 rounded-lg hover:bg-orange-600 transition-colors font-medium flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>照护者修正答案</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCarerCorrection = () => {
    const result = evaluateCarerSupport();
    const needsAttention = result.status === 'NEEDS_ATTENTION';
    const hasImpacts = result.negativeImpacts.physical || result.negativeImpacts.mental || result.negativeImpacts.financial || result.negativeImpacts.social;
    
    return (
      <div className="p-4 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">照护者答案确认</h2>
          <p className="text-gray-600">请照护者根据实际情况确认答案正确性</p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">系统评估结果回顾</h3>
            
            <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
              <div className="space-y-3 mb-4">
                <div className={`flex items-center justify-between p-3 rounded-lg ${
                  result.supportNeedsMet ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}>
                  <span className="font-medium">支持充分性</span>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm ${result.supportNeedsMet ? 'text-green-700' : 'text-red-700'}`}>
                      {result.supportNeedsMet ? '支持充分' : '支持不足'}
                    </span>
                    <span className={`text-lg ${result.supportNeedsMet ? 'text-green-600' : 'text-red-600'}`}>
                      {result.supportNeedsMet ? '✅' : '❌'}
                    </span>
                  </div>
                </div>
                
                <div className={`flex items-center justify-between p-3 rounded-lg ${
                  hasImpacts ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
                }`}>
                  <span className="font-medium">负面影响</span>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm ${hasImpacts ? 'text-red-700' : 'text-green-700'}`}>
                      {hasImpacts ? '有负面影响' : '无负面影响'}
                    </span>
                    <span className={`text-lg ${hasImpacts ? 'text-red-600' : 'text-green-600'}`}>
                      {hasImpacts ? '❌' : '✅'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={`p-3 rounded-lg text-center ${needsAttention ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'}`}>
                <span className={`font-medium ${needsAttention ? 'text-orange-800' : 'text-green-800'}`}>
                  系统评估：{needsAttention ? '❌ 需要关注' : '✅ 支持良好'}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">照护者确认</h3>
            <p className="text-gray-700 mb-4">
              请照护者根据实际感受，确认以下各项的真实情况：
            </p>
            
            <div className="space-y-4">
              {/* 支持充分性确认 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">支持充分性确认</h4>
                <p className="text-sm text-gray-600 mb-3">作为照护者，您是否觉得自己获得了足够的支持？</p>
                <div className="space-y-2">
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, supportAdequacyCorrect: true }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.supportAdequacyCorrect === true
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.supportAdequacyCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.supportAdequacyCorrect === true && (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">✅ 支持充分，感到满意</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, supportAdequacyCorrect: false }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.supportAdequacyCorrect === false
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.supportAdequacyCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.supportAdequacyCorrect === false && (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">❌ 支持不足，需要更多帮助</span>
                    </div>
                  </button>
                </div>
              </div>
              
              {/* 身体健康影响确认 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">身体健康影响确认</h4>
                <p className="text-sm text-gray-600 mb-3">照护工作是否对您的身体健康造成负面影响？</p>
                <div className="space-y-2">
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, physicalImpactCorrect: true }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.physicalImpactCorrect === true
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.physicalImpactCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.physicalImpactCorrect === true && (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">✅ 身体健康状况良好</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, physicalImpactCorrect: false }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.physicalImpactCorrect === false
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.physicalImpactCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.physicalImpactCorrect === false && (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">❌ 照护工作影响身体健康</span>
                    </div>
                  </button>
                </div>
              </div>
              
              {/* 心理健康影响确认 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">心理健康影响确认</h4>
                <p className="text-sm text-gray-600 mb-3">照护工作是否对您的心理健康造成压力？</p>
                <div className="space-y-2">
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, mentalImpactCorrect: true }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.mentalImpactCorrect === true
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.mentalImpactCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.mentalImpactCorrect === true && (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">✅ 心理状态良好</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, mentalImpactCorrect: false }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.mentalImpactCorrect === false
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.mentalImpactCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.mentalImpactCorrect === false && (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">❌ 照护工作造成心理压力</span>
                    </div>
                  </button>
                </div>
              </div>
              
              {/* 经济影响确认 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">经济影响确认</h4>
                <p className="text-sm text-gray-600 mb-3">照护工作是否对您造成经济负担？</p>
                <div className="space-y-2">
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, financialImpactCorrect: true }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.financialImpactCorrect === true
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.financialImpactCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.financialImpactCorrect === true && (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">✅ 经济状况稳定</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, financialImpactCorrect: false }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.financialImpactCorrect === false
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.financialImpactCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.financialImpactCorrect === false && (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">❌ 照护工作造成经济负担</span>
                    </div>
                  </button>
                </div>
              </div>
              
              {/* 社交影响确认 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">社交影响确认</h4>
                <p className="text-sm text-gray-600 mb-3">照护工作是否影响了您的社交生活？</p>
                <div className="space-y-2">
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, socialImpactCorrect: true }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.socialImpactCorrect === true
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.socialImpactCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.socialImpactCorrect === true && (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">✅ 社交生活正常</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, socialImpactCorrect: false }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.socialImpactCorrect === false
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.socialImpactCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.socialImpactCorrect === false && (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">❌ 照护工作影响社交生活</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => handleCarerCorrection(
              carerCorrection.supportAdequacyCorrect!,
              carerCorrection.physicalImpactCorrect!,
              carerCorrection.mentalImpactCorrect!,
              carerCorrection.financialImpactCorrect!,
              carerCorrection.socialImpactCorrect!
            )}
            disabled={
              carerCorrection.supportAdequacyCorrect === null ||
              carerCorrection.physicalImpactCorrect === null ||
              carerCorrection.mentalImpactCorrect === null ||
              carerCorrection.financialImpactCorrect === null ||
              carerCorrection.socialImpactCorrect === null
            }
            className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 disabled:bg-gray-300 transition-colors font-medium"
          >
            确认修正，完成评估
          </button>
        </div>
      </div>
    );
  };

  const renderCompleted = () => (
    <div className="text-center p-4">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">照护者支持评估完成</h2>
      <p className="text-gray-600">正在转到下一项...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto">
        {(() => {
          switch (currentStep) {
            case 'intro': return renderIntro();
            case 'adequacy_question': return renderAdequacyQuestion();
            case 'confidence_question': return renderConfidenceQuestion();
            case 'impact_question': return renderImpactQuestion();
            case 'results': return renderResults();
            case 'carer_correction': return renderCarerCorrection();
            case 'completed': return renderCompleted();
            default: return null;
          }
        })()}
      </div>
    </div>
  )
};

export default CarerSupportAssessment;
